
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Briefcase, MapPin, Clock, DollarSign, Building } from 'lucide-react';
import { toast } from 'sonner';
import JobPostingForm from './JobPostingForm';
import JobsTable from './JobsTable';
import JobApplicationModal from './JobApplicationModal';

interface Job {
  id: string;
  job_title: string;
  company_name: string;
  job_description: string;
  requirements: string | null;
  salary_range: string | null;
  job_type: string;
  location_type: string;
  state: string;
  local_government: string;
  contact_email: string;
  contact_phone: string | null;
  website_url: string | null;
  posted_by: string | null;
  approved: boolean;
  created_at: string;
  expires_at: string | null;
}

interface JobsSectionProps {
  isAdmin: boolean;
}

const JobsSection = ({ isAdmin }: JobsSectionProps) => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPostingFormOpen, setIsPostingFormOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [isAdmin]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('community_jobs')
        .select('*')
        .order('created_at', { ascending: false });

      // If not admin, only show approved jobs
      if (!isAdmin) {
        query = query.eq('approved', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching jobs:', error);
        toast.error('Failed to load jobs');
        return;
      }

      setJobs(data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleJobPosted = () => {
    fetchJobs();
    setIsPostingFormOpen(false);
    toast.success('Job posted successfully! It will be visible after admin approval.');
  };

  const handleToggleApproval = async (jobId: string, currentStatus: boolean) => {
    if (!isAdmin) return;

    try {
      const { error } = await supabase
        .from('community_jobs')
        .update({ approved: !currentStatus })
        .eq('id', jobId);

      if (error) throw error;

      setJobs(prev => prev.map(job => 
        job.id === jobId ? { ...job, approved: !currentStatus } : job
      ));

      toast.success(`Job ${!currentStatus ? 'approved' : 'unapproved'} successfully`);
    } catch (error) {
      console.error('Error toggling job approval:', error);
      toast.error('Failed to update job approval status');
    }
  };

  const handleApplyToJob = (job: Job) => {
    if (!user) {
      toast.error('Please log in to apply for jobs');
      return;
    }
    setSelectedJob(job);
    setIsApplicationModalOpen(true);
  };

  const handleApplicationSubmitted = () => {
    setIsApplicationModalOpen(false);
    setSelectedJob(null);
    toast.success('Application submitted successfully!');
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-8 h-8 border-4 border-omoduduwa-terracotta border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Loading job opportunities...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="text-omoduduwa-terracotta" />
            Job Opportunities
          </h2>
          <p className="text-gray-700 mt-1 font-medium">
            Find and post job opportunities within the Ọmọ Odùduwà community
          </p>
        </div>
        
        {user && (
          <Button 
            onClick={() => setIsPostingFormOpen(true)}
            className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white font-semibold"
          >
            <Plus size={16} className="mr-2" />
            Post Job
          </Button>
        )}
      </div>

      {jobs.length === 0 ? (
        <Card className="bg-white/90 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <Briefcase size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Job Postings Yet</h3>
            <p className="text-gray-700 mb-4 font-medium">
              Be the first to post a job opportunity for the community!
            </p>
            {user && (
              <Button 
                onClick={() => setIsPostingFormOpen(true)}
                className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 text-white font-semibold"
              >
                Post First Job
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <JobsTable 
          jobs={jobs}
          isAdmin={isAdmin}
          onToggleApproval={handleToggleApproval}
          onApplyToJob={handleApplyToJob}
        />
      )}

      <JobPostingForm 
        open={isPostingFormOpen}
        onOpenChange={setIsPostingFormOpen}
        onJobPosted={handleJobPosted}
      />

      {selectedJob && (
        <JobApplicationModal
          open={isApplicationModalOpen}
          onOpenChange={setIsApplicationModalOpen}
          job={selectedJob}
          onApplicationSubmitted={handleApplicationSubmitted}
        />
      )}
    </div>
  );
};

export default JobsSection;
